import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building, HeartPulse, Home, UtensilsCrossed, GraduationCap, Factory,
  ArrowRight, Sparkles, Check,
} from 'lucide-react';
import { useSiteI18n } from '../SiteI18n';
import { SiteSEO } from '../SiteSEO';
import { SiteCtaBlock } from '../components/SitePageParts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import pillarCity from '../assets/pillar-city.jpg';
import pillarCare from '../assets/pillar-care.jpg';
import pillarBuilding from '../assets/pillar-building.jpg';
import pillarFinance from '../assets/pillar-finance.jpg';
import pillarIntelligence from '../assets/pillar-intelligence.jpg';
import pillarSecure from '../assets/pillar-secure.jpg';

const data = (lang: 'ro' | 'en') => lang === 'ro' ? [
  {
    icon: Building, img: pillarCity,
    tint: 'from-cyan-500/30 to-blue-500/10 border-cyan-400/30 text-cyan-300',
    t: 'Administrație publică', d: 'Primării, ministere, regii autonome — Smart City complet operațional în 90 de zile.',
    bullets: ['Trafic + parcări + iluminat', 'Cetățean digital + tickets', 'Raportare CCR/AEP', 'Audit complet & RLS'],
    cta: 'Soluții pentru orașe', href: '/site/solutii#city',
  },
  {
    icon: HeartPulse, img: pillarCare,
    tint: 'from-emerald-500/30 to-teal-500/10 border-emerald-400/30 text-emerald-300',
    t: 'Sănătate', d: 'Spitale, clinici private, cabinete — telemedicină criptată E2E și AI clinic auditat.',
    bullets: ['Medic AI', 'Pacient X & follow-up', 'Documente clinice oficiale', 'Integrare CNAS & ANAF'],
    cta: 'Soluții clinice', href: '/site/solutii#care',
  },
  {
    icon: Home, img: pillarBuilding,
    tint: 'from-violet-500/30 to-fuchsia-500/10 border-violet-400/30 text-violet-300',
    t: 'Imobiliare & dezvoltatori', d: 'KNX nativ, BMS cloud, energie, securitate, confort — brand-ul tău în PWA-ul rezidentului.',
    bullets: ['KNX/IP local + cloud', 'EMS + solar + EV', 'Asociație de proprietari', 'White-label complet'],
    cta: 'Smart Building', href: '/site/solutii#building',
  },
  {
    icon: UtensilsCrossed, img: pillarFinance,
    tint: 'from-sky-500/30 to-cyan-500/10 border-sky-400/30 text-sky-300',
    t: 'Hospitality & retail', d: 'Hoteluri, restaurante, centre comerciale — operațiuni unificate într-un singur dashboard.',
    bullets: ['Facturare + încasări AI', 'Stocuri + NIR + retur', 'KPI dashboard', 'Integrare Stripe & ANAF'],
    cta: 'ERP & Finance', href: '/site/solutii#finance',
  },
  {
    icon: GraduationCap, img: pillarIntelligence,
    tint: 'from-indigo-500/30 to-blue-500/10 border-indigo-400/30 text-indigo-300',
    t: 'Educație', d: 'Universități, școli — clădiri smart, comunitate digitală, comunicare modernă cu părinții.',
    bullets: ['Smart Building integrat', 'ChatAI 24/7 pentru elevi', 'Securitate clinică', 'PWA pentru părinți'],
    cta: 'Cere demo', href: '/site/contact',
  },
  {
    icon: Factory, img: pillarSecure,
    tint: 'from-amber-500/30 to-orange-500/10 border-amber-400/30 text-amber-300',
    t: 'Industrie & operatori NIS2', d: 'Fabrici, depozite, operatori esențiali — KNX/SCADA, IoT, mentenanță predictivă AI.',
    bullets: ['Mentenanță predictivă', 'NIS2 dashboard live', 'Audit & raportare DNSC', 'KNX + Modbus + BACnet'],
    cta: 'Cybersecurity', href: '/site/solutii#secure',
  },
] : [
  {
    icon: Building, img: pillarCity,
    tint: 'from-cyan-500/30 to-blue-500/10 border-cyan-400/30 text-cyan-300',
    t: 'Public administration', d: 'City halls, ministries, public utilities — full Smart City live in 90 days.',
    bullets: ['Traffic + parking + lighting', 'Digital citizen + tickets', 'Auditor reporting', 'Full audit & RLS'],
    cta: 'City solutions', href: '/site/solutii#city',
  },
  {
    icon: HeartPulse, img: pillarCare,
    tint: 'from-emerald-500/30 to-teal-500/10 border-emerald-400/30 text-emerald-300',
    t: 'Healthcare', d: 'Hospitals, private clinics, practices — E2E encrypted telemedicine and audited clinical AI.',
    bullets: ['Medic AI', 'Patient X & follow-up', 'Official clinical PDFs', 'Insurer & tax integrations'],
    cta: 'Clinical solutions', href: '/site/solutii#care',
  },
  {
    icon: Home, img: pillarBuilding,
    tint: 'from-violet-500/30 to-fuchsia-500/10 border-violet-400/30 text-violet-300',
    t: 'Real estate & developers', d: 'Native KNX, cloud BMS, energy, security, comfort — your brand inside the resident PWA.',
    bullets: ['Local KNX/IP + cloud', 'EMS + solar + EV', 'Owner association', 'Full white-label'],
    cta: 'Smart Building', href: '/site/solutii#building',
  },
  {
    icon: UtensilsCrossed, img: pillarFinance,
    tint: 'from-sky-500/30 to-cyan-500/10 border-sky-400/30 text-sky-300',
    t: 'Hospitality & retail', d: 'Hotels, restaurants, malls — unified operations in a single dashboard.',
    bullets: ['AI invoicing + collections', 'Inventory + receipts + returns', 'KPI dashboard', 'Stripe & tax integrations'],
    cta: 'ERP & Finance', href: '/site/solutii#finance',
  },
  {
    icon: GraduationCap, img: pillarIntelligence,
    tint: 'from-indigo-500/30 to-blue-500/10 border-indigo-400/30 text-indigo-300',
    t: 'Education', d: 'Universities, schools — smart buildings, digital community, modern parent communication.',
    bullets: ['Integrated Smart Building', '24/7 ChatAI for students', 'Clinical-grade security', 'Parent PWA'],
    cta: 'Request demo', href: '/site/contact',
  },
  {
    icon: Factory, img: pillarSecure,
    tint: 'from-amber-500/30 to-orange-500/10 border-amber-400/30 text-amber-300',
    t: 'Industry & NIS2 operators', d: 'Factories, warehouses, essential operators — KNX/SCADA, IoT, predictive AI maintenance.',
    bullets: ['Predictive maintenance', 'Live NIS2 dashboard', 'CSIRT reporting', 'KNX + Modbus + BACnet'],
    cta: 'Cybersecurity', href: '/site/solutii#secure',
  },
];

const SiteIndustrii: React.FC = () => {
  const { lang, t } = useSiteI18n();
  const items = data(lang);

  return (
    <>
      <SiteSEO
        path="/site/industrii"
        title={lang === 'ro' ? 'Industrii AETHERIS — Smart City, Sănătate, Real Estate, Hospitality, Industrie' : 'AETHERIS Industries — Smart City, Healthcare, Real Estate, Hospitality, Industry'}
        description={lang === 'ro'
          ? 'Soluții AETHERIS adaptate fiecărei verticale: primării, spitale, dezvoltatori, retail, educație, industrie. AI, IoT, KNX, telemedicină.'
          : 'AETHERIS solutions tailored to each vertical: cities, hospitals, developers, retail, education, industry. AI, IoT, KNX, telemedicine.'}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[550px] bg-gradient-to-br from-primary/25 via-violet-500/15 to-cyan-500/15 rounded-full blur-[130px] site-aurora" />
          <div className="absolute inset-0 site-grid-bg" />
        </div>
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-5 bg-primary/10 text-primary border border-primary/30 backdrop-blur-md">
              <Sparkles className="h-3 w-3 mr-1.5" /> {t.industries.label}
            </Badge>
            <h1 className="text-4xl md:text-7xl font-black tracking-tight leading-[0.98] mb-5">
              <span className="bg-gradient-to-br from-foreground via-foreground to-foreground/40 bg-clip-text text-transparent block">
                {lang === 'ro' ? 'Construită pentru cei' : 'Built for those'}
              </span>
              <span className="bg-gradient-to-br from-primary via-primary to-cyan-400 bg-clip-text text-transparent block">
                {lang === 'ro' ? 'care decid.' : 'who decide.'}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {lang === 'ro'
                ? 'Fiecare industrie are propriile reguli, KPI-uri și constrângeri. AETHERIS livrează exact ce ai nevoie — fără modificări inutile.'
                : 'Every industry has its own rules, KPIs and constraints. AETHERIS delivers exactly what you need — no unnecessary tweaks.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it, i) => (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card/50 backdrop-blur-md hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 transition-all"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={it.img} alt={it.t} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${it.tint.split(' ').slice(0, 2).join(' ')} mix-blend-overlay`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                  <div className={`absolute top-3 right-3 h-11 w-11 rounded-2xl bg-card/60 border backdrop-blur-md grid place-items-center ${it.tint}`}>
                    <it.icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-2">{it.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{it.d}</p>
                  <ul className="space-y-1.5 mb-5">
                    {it.bullets.map((b) => (
                      <li key={b} className="text-xs text-foreground/80 flex items-center gap-2">
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={it.href}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  >
                    {it.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="shadow-xl shadow-primary/30">
              <Link to="/site/contact" className="gap-2">
                {t.nav.demo} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* DETAILED CAPABILITY CATALOG per vertical */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Catalog de capabilități' : 'Capability catalog'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'Hardware + software, integrate nativ.' : 'Hardware + software, natively integrated.'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'ro'
                ? 'Pentru fiecare verticală, AETHERIS livrează un set complet de soluții de automatizare, monitorizare și control — activate modular.'
                : 'For each vertical, AETHERIS delivers a complete set of automation, monitoring and control solutions — modularly activated.'}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-5 max-w-6xl mx-auto">
            {([
              {
                t: lang === 'ro' ? 'Birouri · administrații · educație · retail · spitale' : 'Offices · administrations · education · retail · hospitals',
                tag: lang === 'ro' ? 'Building automation (HW & SW)' : 'Building automation (HW & SW)',
                items: lang === 'ro' ? [
                  'Iluminat smart & control ocupare', 'HVAC & calitate aer', 'Booking săli & automatizare',
                  'Control acces & gestiune vizitatori', 'Eficiență energetică & monitorizare', 'Control jaluzele & umbrire',
                  'Securitate & supraveghere', 'Digital signage & afișaje', 'Dashboard-uri unificate', 'Smart workstation management',
                ] : [
                  'Smart lighting & occupancy control', 'HVAC & air quality management', 'Meeting room booking & automation',
                  'Access control & visitor management', 'Energy efficiency & monitoring', 'Blinds & shading control',
                  'Security & surveillance', 'Digital signage & display control', 'Unified control dashboards', 'Smart workstation management',
                ],
              },
              {
                t: lang === 'ro' ? 'Industrie & site-uri industriale' : 'Industry & industrial sites',
                tag: lang === 'ro' ? 'Industrial automation (HW & SW)' : 'Industrial automation (HW & SW)',
                items: lang === 'ro' ? [
                  'Automatizare procese', 'Control mașini & robotică', 'SCADA & PLC systems',
                  'Monitorizare & optimizare energie', 'Mentenanță predictivă', 'Monitorizare ambientală (aer, temp, umiditate)',
                  'Sisteme de acces & siguranță', 'Iluminat & HVAC automation', 'Data logging & analytics', 'Industrial IoT integration',
                ] : [
                  'Process automation', 'Machine control & robotics', 'SCADA & PLC systems',
                  'Energy monitoring & optimisation', 'Predictive maintenance', 'Environmental monitoring (air, temp, humidity)',
                  'Access & safety systems', 'Lighting & HVAC automation', 'Data logging & analytics', 'Industrial IoT integration',
                ],
              },
              {
                t: lang === 'ro' ? 'Hoteluri & hospitality' : 'Hotels & hospitality',
                tag: 'GRMS · BMS · Guest experience',
                items: lang === 'ro' ? [
                  'Guest Room Management Systems (GRMS)', 'Control iluminat & climat', 'Smart check-in / check-out',
                  'Acces & keyless entry', 'Mod economisire energie pe bază ocupare', 'Entertainment & room service',
                  'Centralized building management', 'Automatizare lobby & spații comune', 'Workflow staff', 'Sisteme de urgență & siguranță',
                ] : [
                  'Guest Room Management Systems (GRMS)', 'Lighting & climate control', 'Smart check-in / check-out',
                  'Access control & keyless entry', 'Occupancy-based energy saving modes', 'Entertainment & room service integration',
                  'Centralized building management', 'Lobby & common area automation', 'Staff workflow automation', 'Emergency & safety systems',
                ],
              },
              {
                t: lang === 'ro' ? 'Smart City · administrație urbană' : 'Smart City · urban administration',
                tag: lang === 'ro' ? 'City automation (HW & SW)' : 'City automation (HW & SW)',
                items: lang === 'ro' ? [
                  'Iluminat stradal smart', 'Trafic & management parcări', 'Siguranță publică & supraveghere',
                  'Monitorizare ambientală (aer, zgomot, apă)', 'Automatizare gestiune deșeuri', 'Smart grid & distribuție energie',
                  'Automatizare transport public', 'Management apă & utilități', 'Mobilitate urbană & încărcare EV', 'Platforme de implicare cetățeni',
                ] : [
                  'Smart street lighting', 'Traffic & parking management', 'Public safety & surveillance',
                  'Environmental monitoring (air, noise, water)', 'Waste management automation', 'Smart grid & energy distribution',
                  'Public transport automation', 'Water & utility management', 'Urban mobility & EV charging', 'Citizen engagement platforms',
                ],
              },
            ]).map((cat, i) => (
              <motion.div
                key={cat.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: i * 0.06 }}
                className="group relative rounded-3xl border border-border/60 bg-card/60 backdrop-blur-md p-7 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary/15 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity" />
                <div className="relative">
                  <Badge className="mb-3 bg-primary/10 text-primary border border-primary/30 text-[10px]">{cat.tag}</Badge>
                  <h3 className="font-bold text-lg mb-4">{cat.t}</h3>
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {cat.items.map((it) => (
                      <li key={it} className="text-xs text-muted-foreground flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SiteCtaBlock />
    </>
  );
};
export default SiteIndustrii;
